// src/lib/we-anzeige.ts — reine Anzeige-Ableitungen der WE-Auftragsdetailseite.
//
/** Antwortfelder von POST /warehouse/we/items/:id/paletten/, die ueber den Ablage-Hinweis
 *  entscheiden. Spiegel von WeReportPalettesResponse (api/warehouse.ts). */
export interface WeUploadHinweis {
  /** 'ok' = Ablage belegt. 'warnung' = Portal-Download ist der verlaessliche Weg. */
  art: 'ok' | 'warnung'
  text: string
}

/** Der aktuelle Server bestaetigt beide Graph-PUTs mit upload_sent und onedrive_path.
 *  Aeltere Server nutzten upload_sent schon fuer eine Webhook-Annahme. Nur beide
 *  Antwortfelder zusammen belegen deshalb die fertige Ablage. */
export function weUploadHinweis(uploadSent: boolean, onedrivePath?: string | null): WeUploadHinweis {
  const pfad = typeof onedrivePath === 'string' ? onedrivePath.trim() : ''
  if (uploadSent && pfad) return { art: 'ok', text: `PDFs in OneDrive abgelegt: ${pfad}` }
  if (uploadSent) {
    return {
      art: 'warnung',
      text: 'Druckauftrag übermittelt — Ablage in OneDrive noch nicht bestätigt; PDFs hier im Portal drucken',
    }
  }
  return { art: 'warnung', text: 'Kein OneDrive-Upload — PDFs hier im Portal drucken' }
}

/** Drill-down-Zeile eines Paletten-Etiketts (bookedInfo[labelId] aus GET /receipt/:id/). */
export interface WeEtikettBuchung {
  placeBarcode: string | null
  bookedBy: string | null
  bookedAt: string | null
}

export interface WeEtikettZeile {
  /** Stueckzahl der Palette. V2-Etiketten tragen qty=NULL bis zur Erfassung am Regal
   *  (Migration 0088) — dann gibt es noch keine Zahl, die man zeigen koennte. */
  menge: string
  /** Offen oder gebucht; gebucht mit Regalplatz, Bearbeiter und Datum. */
  zustand: string
}

const UNBEKANNT = '?'

/** `formatDatum` ist einspeisbar, damit der Test nicht von Zeitzone und Intl-Daten der
 *  Laufzeitumgebung abhaengt (Muster ladeOffeneKommissionierAnzahl in der Scan-App). */
export function weEtikettZeile(
  label: { status: string; qty: number | null; cancelReason: string | null },
  gebucht: WeEtikettBuchung | undefined,
  formatDatum: (iso: string) => string = (iso) => new Date(iso).toLocaleString('de-DE'),
): WeEtikettZeile {
  const menge = label.qty != null ? `${label.qty} St.` : '— (am Regal)'
  if (label.status === 'cancelled') {
    return { menge, zustand: `storniert: ${label.cancelReason ?? UNBEKANNT}` }
  }
  if (label.status !== 'booked') return { menge, zustand: '⏳ offen' }
  // Gebucht, aber die Herkunftsdaten fehlen: das ist ein echter Datenbefund (Position
  // oder place-Move nicht auffindbar, service.ts:536) und wird als Luecke gezeigt, nicht
  // durch Weglassen der Spalte verdeckt.
  const teile = [
    '✅ gebucht',
    gebucht?.placeBarcode ?? UNBEKANNT,
    gebucht?.bookedBy ?? UNBEKANNT,
    gebucht?.bookedAt ? formatDatum(gebucht.bookedAt) : UNBEKANNT,
  ]
  return { menge, zustand: teile.join(' · ') }
}
